import { Box, Flex, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { flexRender, Table as ReactTable } from '@tanstack/react-table';
import * as _ from 'lodash';
import React from 'react';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  frozenHeaderRowStyling,
  frozenMultipleHeaderCellStyling,
  frozenMultipleRowCellStyling,
  frozenTableStyling,
} from 'packages/common-frontend/styles';

type BasicTableProps = {
  table: ReactTable<any>;
  frozenColumnIds?: string[];
  maxHeight?: string;
};
export const BasicTable = ({
  table,
  frozenColumnIds = [],
  maxHeight = '50vh',
}: BasicTableProps) => {
  const frozenTableColumns = table
    .getHeaderGroups()[0]
    .headers.filter((header) => frozenColumnIds.includes(header.column.id));
  const frozenTableColumnWidths = frozenTableColumns.map((header) => ({
    id: header.column.id,
    width: header.getSize(),
  }));

  const getFrozenColumnWidthBeforeColumn = (
    columnId: string,
    columns: { id: string; width: number }[],
  ) => {
    const index = frozenColumnIds.indexOf(columnId);
    if (index === -1) {
      return 0;
    }
    return columns.slice(0, index).reduce((acc, { width }) => acc + width, 0);
  };

  return (
    <Box
      borderWidth="1px"
      borderColor={colors.gray[200]}
      overflowX="auto"
      overflowY="auto"
      position="relative"
      maxHeight={maxHeight}
    >
      <Table style={frozenTableStyling}>
        <Thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();
                return (
                  <Th
                    key={header.id}
                    textTransform="capitalize"
                    letterSpacing="normal"
                    padding="8px 16px 8px 16px"
                    position="sticky"
                    top="0"
                    borderBottomWidth="thin"
                    minW={header.column.getSize()}
                    maxW={header.column.getSize()}
                    backgroundColor={colors.background[100]}
                    style={frozenMultipleHeaderCellStyling(
                      frozenColumnIds.includes(header.id),
                      getFrozenColumnWidthBeforeColumn(
                        header.id,
                        frozenTableColumnWidths,
                      ),
                    )}
                  >
                    {header.isPlaceholder ? null : (
                      <Flex alignItems="center">
                        <Box
                          cursor={columnCanSort ? 'pointer' : 'default'}
                          onClick={header.column.getToggleSortingHandler()}
                          mr={columnCanSort ? '8px' : '0px'}
                        >
                          {{
                            asc: <Icon icon="sort-up" />,
                            desc: <Icon icon="sort-down" />,
                          }[header.column.getIsSorted() as string] ??
                            (columnCanSort ? <Icon icon="sort" /> : null)}
                        </Box>

                        <Box mr="8px">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Box>
                      </Flex>
                    )}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody backgroundColor="white">
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  textAlign={cell.column.columnDef.meta?.style?.textAlign}
                  padding={
                    cell.column.columnDef.meta?.style?.padding || '8px 16px'
                  }
                  height="56px"
                  minW={cell.column.getSize()}
                  maxW={cell.column.getSize()}
                  style={frozenMultipleRowCellStyling(
                    frozenColumnIds.includes(cell.column.id),
                    getFrozenColumnWidthBeforeColumn(
                      cell.column.id,
                      frozenTableColumnWidths,
                    ),
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};
